// components/clients/ClientModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import { Business, AccessTime, AttachMoney } from '@mui/icons-material';

const ClientModal = ({ 
  open, 
  onClose, 
  onSave, 
  client = null, 
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    razon_social: '',
    horas: '',
    monto_base: ''
  });
  const [errors, setErrors] = useState({});

  // Resetear formulario cuando se abre/cierra el modal
  useEffect(() => {
    if (open) {
      if (client) {
        // Modo edición - cargar datos del cliente
        setFormData({
          razon_social: client.razon_social || '',
          horas: client.horas?.toString() || '',
          monto_base: client.monto_base?.toString() || ''
        });
      } else {
        // Modo creación - limpiar formulario
        setFormData({
          razon_social: '',
          horas: '',
          monto_base: ''
        });
      }
      setErrors({});
    }
  }, [open, client]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.razon_social.trim()) {
      newErrors.razon_social = 'La razón social es requerida';
    }

    if (!formData.horas) {
      newErrors.horas = 'La cantidad de horas es requerida';
    } else if (parseFloat(formData.horas) <= 0) {
      newErrors.horas = 'Las horas deben ser mayor a 0';
    }

    if (!formData.monto_base) {
      newErrors.monto_base = 'El monto base es requerido';
    } else if (parseFloat(formData.monto_base) <= 0) {
      newErrors.monto_base = 'El monto base debe ser mayor a 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const dataToSend = {
      razon_social: formData.razon_social.trim(),
      horas: parseFloat(formData.horas),
      monto_base: parseFloat(formData.monto_base)
    };

    // Si estamos editando, incluir el ID
    if (client?.id) {
      dataToSend.id = client.id;
    }

    onSave(dataToSend);
  };

  const handleChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const isEditing = !!client;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {isEditing ? '✏️ Editar Cliente' : '➕ Agregar Nuevo Cliente'}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Razón Social"
                value={formData.razon_social}
                onChange={handleChange('razon_social')}
                error={!!errors.razon_social}
                helperText={errors.razon_social}
                disabled={loading}
                required
                InputProps={{
                  startAdornment: <Business sx={{ mr: 1, color: 'action.active' }} />
                }}
                placeholder="Ej: Empresa ABC S.A."
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Cantidad de Horas"
                value={formData.horas}
                onChange={handleChange('horas')}
                error={!!errors.horas}
                helperText={errors.horas}
                disabled={loading}
                required
                inputProps={{ min: 0, step: 0.5 }}
                InputProps={{
                  startAdornment: <AccessTime sx={{ mr: 1, color: 'action.active' }} />
                }}
                placeholder="Ej: 160"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Monto Base (Sin IVA)"
                value={formData.monto_base}
                onChange={handleChange('monto_base')}
                error={!!errors.monto_base}
                helperText={errors.monto_base}
                disabled={loading}
                required
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: <AttachMoney sx={{ mr: 1, color: 'action.active' }} />
                }}
                placeholder="Ej: 100000"
              />
            </Grid>
          </Grid>

          {Object.keys(errors).length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Por favor corrija los errores antes de continuar
            </Alert>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={onClose} 
            disabled={loading}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button 
            type="submit"
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear Cliente')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ClientModal;